import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { t, styled } from "@superset-ui/core";
import { InfoTooltipWithTrigger } from "@superset-ui/chart-controls";
import { Row, Col, Select } from "src/components";
import { Input, InputNumber } from "src/components/Input";
import Button from "src/components/Button";
import { useSelector } from "react-redux";
import { RootState } from "src/views/store";
import { SelectOptionsType } from "src/components/Select/types";
import ControlHeader from "src/explore/components/ControlHeader";
import {
    CloseCircleFilled,
    UploadOutlined,
    StopOutlined,
    WarningOutlined,
    CheckCircleOutlined,
} from "@ant-design/icons";
import ControlPopover from "../../ControlPopover/ControlPopover";
import CheckboxControl from "../../CheckboxControl";

export interface MetricRangeWidthImageControlProps {
    selectedMetric: string;
    selectedMetricTitle: string;
    popoverVisible: boolean;
    metrics: SelectOptionsType;
    useIcon: boolean;
    uploads: string;
    iconLabel: string;
    onChange: (changeObj: MetricRangeWidthImageControlState) => void;
}

export interface MetricRangeWidthImageControlState {
    selectedMetric: string | undefined;
    selectedMetricTitle: string;
    popoverVisible: boolean;
    metrics: SelectOptionsType;
    useIcon: boolean;
    iconLabel: string;
    uploads: string;
    uploadCount: number;
}

interface FileUpload {
    id: number;
    file?: string | ArrayBuffer | null;
    fileName?: string;
    min?: number;
    max?: number;
}

const StyledRow = styled(Row)`
    margin-top: ${({ theme }) => theme.gridUnit * 2}px;
    display: flex;
    align-items: center;
`;

const StyledCol = styled(Col)`
    display: flex;
    align-items: center;
`;

const StyledTooltip = styled(InfoTooltipWithTrigger)`
    margin-left: ${({ theme }) => theme.gridUnit}px;
    color: ${({ theme }) => theme.colors.grayscale.light1};
`;

const ButtonBar = styled.div`
    margin-top: ${({ theme }) => theme.gridUnit * 5}px;
    display: flex;
    justify-content: center;
`;

const StyledUploadContainer = styled.div`
    input[type="file"] {
        display: none;
    }
    ${({ theme }) => `
        position: relative;
        margin: 8px 0;
        padding: 8px;
        border-top: 1px solid ${theme.colors.grayscale.light2};
    `}
`;

const StyledMinMaxInputRow = styled.div`
    display: flex;
    gap: 8px;
`;

const StyledFileInputDiv = styled.div`
    position: relative;
`;

const StyledButtonContainer = styled.div`
    ${({ theme }) => `
        display: flex;
        justify-content: flex-end;
        position: absolute;
        width: 24px;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        gap: 4px;
        color: ${theme.colors.icon.base}
    `}
`;

const StyledDeleteButton = styled(CloseCircleFilled)`
    ${({ theme }) => `
        cursor: pointer;
        &:hover {
            color: ${theme.colors.icon.hover}
        }
    `}
`;

const StyledUploadButton = styled(UploadOutlined)`
    ${({ theme }) => `
        cursor: pointer;
        &:hover {
            color: ${theme.colors.icon.hover}
        }
    `}
`;

const StyledInput = styled(Input)`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding-right: 40px;
`;

const StyledInputNumber = styled(InputNumber)`
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding-right: 40px;
`;

const StyledSummarySpan = styled.span`
    margin-right: 5px;
    > span {
        margin-right: 5px;
    }
`;

const StyledStopOutlined = styled(StopOutlined)`
    color: ${({ theme }) => theme.colors.error.base};
`;

const StyledWarningOutlined = styled(WarningOutlined)`
    color: ${({ theme }) => theme.colors.alert.base};
`;

const StyledCheckCircleOutlined = styled(CheckCircleOutlined)`
    color: ${({ theme }) => theme.colors.success.base};
`;

const MetricRangeWidthImageControl = (props: MetricRangeWidthImageControlProps) => {
    const { selectedMetric, selectedMetricTitle, uploads, useIcon, onChange, iconLabel } = props;

    const storedMetricData = useSelector((state: RootState) => state.explore.controls.metrics.value);

    const fileInputRefs = useRef<HTMLInputElement[]>([]);
    const [uploadsState, setUploadsState] = useState<FileUpload[]>([]);
    const [uploadCount, setUploadCount] = useState<number>(1);
    const MAX_COUNT = 10;

    const [currentState, setCurrentState] = useState<MetricRangeWidthImageControlState>();
    const [initialState] = useState<MetricRangeWidthImageControlState>({
        selectedMetric: selectedMetric || undefined,
        selectedMetricTitle,
        popoverVisible: false,
        metrics: [],
        useIcon: useIcon === undefined ? false : useIcon,
        uploads,
        iconLabel,
        uploadCount,
    });

    useEffect(() => {
        if (storedMetricData) {
            const metricsInfo = storedMetricData.map((dataset: any) => ({
                label: dataset.label,
                value: dataset.label,
                key: dataset.label,
            }));
            setCurrentState({ ...initialState, metrics: metricsInfo });
        }
    }, [storedMetricData]);

    useEffect(() => {
        if (uploads) {
            const parseJsonFileData = JSON.parse(uploads);
            setUploadsState(parseJsonFileData);
            setUploadCount(parseJsonFileData.length);
        }
    }, [uploads]);

    const resetState = (): void => {
        setCurrentState({ ...initialState });
    };

    const onSave = (): void => {
        if (currentState) {
            onChange(currentState);
            setCurrentState({ ...currentState, popoverVisible: false });
        }
    };

    const onClose = (): void => {
        if (currentState) {
            setCurrentState({ ...currentState, popoverVisible: false });
        }
    };

    const onPopoverVisibleChange = (popoverVisible: boolean): void => {
        if (popoverVisible && currentState) {
            setCurrentState({ ...currentState, popoverVisible });
        } else {
            resetState();
        }
    };

    const textSummary = (): string => {
        if (currentState) {
            if (storedMetricData && !currentState.selectedMetric) {
                return t("Metrics column not set");
            }
            if (currentState.selectedMetricTitle) {
                return currentState.selectedMetricTitle;
            }

            if (currentState.selectedMetric) {
                return currentState.selectedMetric;
            }
        }
        return t("Metrics column not selected");
    };

    const formRow = (label: string, tooltip: string, control: JSX.Element) => (
        <StyledRow>
            <StyledCol xs={24} md={11}>
                {label}
                <StyledTooltip placement="top" tooltip={tooltip} />
            </StyledCol>
            <Col xs={24} md={13}>
                {control}
            </Col>
        </StyledRow>
    );

    /** 업로드 처리 영역 */
    /**
     * @description [useEffect hooks] uploadsState가 변경되는 경우 전송용 currentState에 새로 저장
     */
    useEffect(() => {
        if (uploadsState && currentState) {
            setCurrentState({ ...currentState, uploads: JSON.stringify(uploadsState) });
        }
    }, [uploadsState]);

    /**
     * @name handleUploadCountChange
     * @function
     * @description 업로드 컨트롤 생성 갯수 변경 이벤트
     * @return {void}
     */
    const handleUploadCountChange = (value: number): void => {
        if (value > 0 && value <= MAX_COUNT) {
            const updatedUploads = [...uploadsState];
            const numberOfObjectToCut = updatedUploads.length - value;
            if (numberOfObjectToCut > 0) {
                const cutUpdatedUploads = updatedUploads.slice(0, updatedUploads.length - numberOfObjectToCut);
                setUploadsState(cutUpdatedUploads);
            }
            setUploadCount(value);
        }
    };

    /**
     * @name handleFileInputChange
     * @function
     * @description 파일 업로드 변경 이벤트
     * @return {void}
     */
    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const { files } = event.target;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();
            if (file) {
                reader.readAsDataURL(file);
                reader.onloadend = () => {
                    const updatedUploads = [...uploadsState];
                    if (updatedUploads[id]) {
                        updatedUploads[id].file = reader.result;
                        updatedUploads[id].fileName = file.name;
                    } else {
                        updatedUploads[id] = {
                            id,
                            file: reader.result,
                            fileName: file.name,
                        };
                    }
                    setUploadsState(updatedUploads);
                };
            }
        }
    };

    /**
     * @name handleMinInputChange
     * @function
     * @description 업로드 컨트롤 최소 값 입력 변경 이벤트
     * @return {void}
     */
    const handleMinInputChange = (uploadId: number, minValue: number): void => {
        const updatedUploads = [...uploadsState];
        if (updatedUploads[uploadId]) {
            updatedUploads[uploadId].min = minValue;
        } else {
            updatedUploads[uploadId] = { id: uploadId, min: minValue };
        }
        setUploadsState(updatedUploads);
    };

    /**
     * @name handleMaxInputChange
     * @function
     * @description 업로드 컨트롤 최대 값 입력 변경 이벤트
     * @return {void}
     */
    const handleMaxInputChange = (uploadId: number, maxValue: number): void => {
        const updatedUploads = [...uploadsState];
        if (updatedUploads[uploadId]) {
            updatedUploads[uploadId].max = maxValue;
        } else {
            updatedUploads[uploadId] = { id: uploadId, max: maxValue };
        }
        setUploadsState(updatedUploads);
    };

    /**
     * @name handleDeleteClick
     * @function
     * @description 삭제 버튼 클릭 이벤트 핸들러
     * @return {void}
     */
    const handleDeleteClick = (uploadId: number): void => {
        fileInputRefs.current[uploadId].value = "";
        const updatedUploads = [...uploadsState];
        updatedUploads[uploadId].file = null;
        updatedUploads[uploadId].fileName = "";
        setUploadsState(updatedUploads);
    };

    /**
     * @name handleUploadClick
     * @function
     * @description 업로드 버튼 클릭 이벤트 핸들러
     * @return {void}
     */
    const handleUploadClick = (uploadId: number): void => {
        fileInputRefs.current[uploadId]?.click();
    };

    const dynamicRenderUploads = () =>
        Array.from({ length: uploadCount }, (_, index) => {
            const uploadId = index;
            return (
                <>
                    {formRow(
                        t("Set Range"),
                        t("Enter range values and upload image (icon) files"),
                        <>
                            <StyledUploadContainer key={uploadId}>
                                <StyledMinMaxInputRow>
                                    <div>
                                        <ControlHeader
                                            {...{
                                                ...props,
                                                label: t("Minimum Range"),
                                                description: t(
                                                    "If the minimum value is an empty value, all values below the maximum value are applied"
                                                ),
                                            }}
                                        />
                                        <StyledInputNumber
                                            aria-label={selectedMetric}
                                            value={uploadsState[uploadId]?.min}
                                            onChange={(value: number) => handleMinInputChange(uploadId, value)}
                                        />
                                    </div>
                                    <div>
                                        <ControlHeader
                                            {...{
                                                ...props,
                                                label: t("Maximum Range"),
                                                description: t(
                                                    "If the maximum value is an empty value, all values above the minimum value are applied"
                                                ),
                                            }}
                                        />
                                        <StyledInputNumber
                                            aria-label={selectedMetric}
                                            value={uploadsState[uploadId]?.max}
                                            onChange={(value: number) => handleMaxInputChange(uploadId, value)}
                                        />
                                    </div>
                                </StyledMinMaxInputRow>
                                <ControlHeader {...{ label: t("Images (icons)") }} />
                                <StyledFileInputDiv>
                                    <StyledInput
                                        placeholder="placeholder"
                                        value={uploadsState[uploadId]?.fileName}
                                        aria-label={selectedMetric}
                                        readOnly
                                    />
                                    <StyledButtonContainer>
                                        {uploadsState[uploadId]?.fileName && (
                                            <StyledDeleteButton size={12} onClick={() => handleDeleteClick(uploadId)} />
                                        )}
                                        <StyledUploadButton size={12} onClick={() => handleUploadClick(uploadId)} />
                                    </StyledButtonContainer>
                                </StyledFileInputDiv>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={(ref) => {
                                        if (ref) {
                                            fileInputRefs.current[uploadId] = ref;
                                        }
                                    }}
                                    onChange={(e) => handleFileInputChange(e, uploadId)}
                                />
                            </StyledUploadContainer>
                        </>
                    )}
                </>
            );
        });

    const renderPopover = (): JSX.Element => (
        <div id="ts-col-popo" style={{ width: 500 }}>
            {currentState && (
                <>
                    {formRow(
                        t("Select Metrics"),
                        t("Select the Metrics column to be detailed"),
                        <Select
                            ariaLabel={t("Metrics")}
                            value={currentState.selectedMetric}
                            onChange={(selectValue: string) =>
                                setCurrentState({
                                    ...currentState,
                                    selectedMetric: selectValue,
                                })
                            }
                            options={currentState.metrics}
                        />
                    )}
                    {formRow(
                        t("Metrics screen display name"),
                        t("Screen display name for the currently selected Metrics column"),
                        <Input
                            value={currentState.selectedMetricTitle}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setCurrentState({ ...currentState, selectedMetricTitle: e.target.value })
                            }
                        />
                    )}
                    {formRow(
                        t("Whether to use images (icons)"),
                        t("Whether to show the image (icon) to the left of the bottom value displayed"),
                        <CheckboxControl
                            value={currentState.useIcon}
                            onChange={(checkValue: boolean) =>
                                setCurrentState({
                                    ...currentState,
                                    useIcon: checkValue,
                                })
                            }
                        />
                    )}
                    {currentState.useIcon && (
                        <>
                            {formRow(
                                t("Icon Label Text"),
                                t("Text to be displayed directly left side of the icon"),
                                <Input
                                    value={currentState.iconLabel}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        setCurrentState({ ...currentState, iconLabel: e.target.value })
                                    }
                                />
                            )}
                            {formRow(
                                t("Set the number of ranges"),
                                t("Set the number of scopes for the currently selected metric"),
                                <StyledInputNumber
                                    value={uploadCount}
                                    min={1}
                                    max={MAX_COUNT}
                                    aria-label={selectedMetric}
                                    onChange={(value: number) => handleUploadCountChange(value)}
                                />
                            )}

                            <div style={{ height: "160px", overflowY: "auto" }}>
                                {uploadCount > 0 && dynamicRenderUploads()}
                            </div>
                        </>
                    )}
                    <ButtonBar>
                        <Button buttonSize="small" onClick={onClose} cta>
                            {t("Close")}
                        </Button>
                        <Button buttonStyle="primary" buttonSize="small" onClick={onSave} cta>
                            {t("Save")}
                        </Button>
                    </ButtonBar>
                </>
            )}
        </div>
    );

    return (
        <span>
            <StyledSummarySpan>
                {!storedMetricData && <StyledStopOutlined />}
                {storedMetricData && !currentState?.selectedMetric && <StyledWarningOutlined />}
                {storedMetricData && currentState?.selectedMetric && <StyledCheckCircleOutlined />}
                {textSummary()}
            </StyledSummarySpan>
            <ControlPopover
                trigger="click"
                content={renderPopover}
                title={t("Column Configuration")}
                visible={currentState?.popoverVisible}
                onVisibleChange={onPopoverVisibleChange}
            >
                <InfoTooltipWithTrigger icon="edit" className="text-primary" label="edit-ts-column" />
            </ControlPopover>
        </span>
    );
};

export default MetricRangeWidthImageControl;
